import type { FC } from 'react';
import { useEffect } from 'react';

import {
	ContentUnifiedQuery,
	ContentBlogUnifiedQuery,
	type ContentUnifiedQueryType,
	type ContentBlogUnifiedQueryType,
	type ContentUnifiedQueryVariablesType,
	type ContentBlogUnifiedQueryVariablesType,
} from '@confluence/content-unified-query';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';
import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient.ts';
import { useHistory } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useContentType } from '@confluence/page-context';

type ContentRefetchHandlerProps = {
	contentId?: string;
	shouldRefetch: boolean;
	refetchImmediately?: boolean;
	onRefetchComplete?: (versionNumber?: number | null) => void;
};

type ChooseContentUnifiedQueryType = ContentUnifiedQueryType | ContentBlogUnifiedQueryType;
type ChooseContentUnifiedQueryVariablesType =
	| ContentUnifiedQueryVariablesType
	| ContentBlogUnifiedQueryVariablesType;

export const ContentRefetchHandler: FC<ContentRefetchHandlerProps> = ({
	contentId,
	shouldRefetch,
	refetchImmediately,
	onRefetchComplete,
}) => {
	const history = useHistory();
	const [contentType] = useContentType();
	const spaceKey = useSpaceKey();

	useEffect(() => {
		let unlisten;
		if (shouldRefetch && contentId) {
			const chooseContentUnifiedQuery =
				contentType === 'blogpost' ? ContentBlogUnifiedQuery : ContentUnifiedQuery;

			const queryVariable: ChooseContentUnifiedQueryVariablesType = {
				contentId,
				spaceKey,
				useNewContentTopper: true,
			};

			const refetchFn = async () => {
				let versionNumber: number | null | undefined;
				try {
					// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
					const queryResult = await getApolloClient().query<
						ChooseContentUnifiedQueryType,
						ChooseContentUnifiedQueryVariablesType
					>({
						query: chooseContentUnifiedQuery,
						context: { single: true },
						fetchPolicy: 'network-only',
						variables: queryVariable,
					});

					// at this time, queryResult.data is always ContentUnifiedQueryType or ContentBlogUnifiedQueryType format
					// @ts-ignore
					versionNumber = queryResult.data.content?.nodes?.[0]?.version?.number;
				} catch {
					// NOTE: We intentionally swallow any possible error here as this query is only aimed to
					// populate the cache with just published content. Should any error occur here after
					// successful publishing the page, the Apollo cache for the query will be invalidated
					// anyway and View Page will fetch the page and handle possible errors.
				}

				// If a callback is supplied, call it regardless
				onRefetchComplete && onRefetchComplete(versionNumber);
			};

			if (refetchImmediately) {
				void refetchFn();
			} else {
				unlisten = history?.listen(refetchFn);
			}
		}

		return () => {
			// Call the unlistener when this component unmounts
			unlisten && unlisten();
		};
		/* eslint-disable-next-line react-hooks/exhaustive-deps */
	}, [shouldRefetch]);

	return null;
};
